import { Breadcrumb } from "@/components/common/Breadcrumb";
import { C } from "@/brand/tokens";

/**
 * InteriorHero — page hero for interior templates.
 *
 * Composition:
 *   - Breadcrumb trail
 *   - Small eyebrow rule + brass label
 *   - Massive Cormorant page title
 *   - Optional supporting paragraph (max 60ch)
 *   - Optional right-column metadata slot (dl-style key/value list)
 *
 * Tones:
 *   - "light" (default) — Dry Stone or Soft Parchment
 *   - "dark"           — Deep Mineral Blue
 */
export function InteriorHero({
  breadcrumb,
  eyebrow,
  title,
  intro,
  meta,
  tone = "light",
}) {
  const isDark = tone === "dark";
  const bg = isDark ? C.surfacePrimary : C.surfaceLight;
  const textColor = isDark ? C.textOnDark : C.textOnLight;
  const bodyColor = isDark ? C.whiteOnDark : C.inkOnLight;
  const bodySoft = isDark ? C.whiteOnDarkSoft : C.inkOnLightSoft;

  return (
    <section
      data-testid="interior-hero"
      style={{
        background: bg,
        color: textColor,
        borderBottom: `1px solid ${isDark ? C.brassSoft : C.borderSoft}`,
      }}
    >
      <div className="mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-12 py-16 lg:py-24">
        {breadcrumb && (
          <div className="mb-12">
            <Breadcrumb trail={breadcrumb} tone={tone} />
          </div>
        )}

        <div className="grid lg:grid-cols-12 gap-10">
          <div className={meta ? "lg:col-span-8" : "lg:col-span-10"}>
            {eyebrow && (
              <div className="flex items-center gap-4 mb-7">
                <span
                  aria-hidden="true"
                  className="inline-block h-px w-14"
                  style={{ background: C.accentMetal }}
                />
                <p
                  className="font-inter text-[11px] uppercase font-semibold"
                  style={{ color: C.accentMetal, letterSpacing: "0.28em" }}
                >
                  {eyebrow}
                </p>
              </div>
            )}
            <h1
              data-testid="interior-title"
              className="font-cormorant"
              style={{
                color: textColor,
                fontSize: "clamp(2.4rem, 5vw, 4.4rem)",
                fontWeight: 400,
                lineHeight: 1.02,
                letterSpacing: "-0.014em",
              }}
            >
              {title}
            </h1>
            {intro && (
              <p
                className="mt-9 font-inter leading-[1.7] max-w-[58ch]"
                style={{
                  color: bodyColor,
                  fontSize: "clamp(1.02rem, 1.15vw, 1.15rem)",
                }}
              >
                {intro}
              </p>
            )}
          </div>

          {meta && (
            <aside className="lg:col-span-4 lg:pt-4">
              <dl
                className="pt-6"
                style={{
                  borderTop: `1px solid ${isDark ? C.brassSoft : C.borderSoft}`,
                }}
              >
                {meta.map(([label, value]) => (
                  <div
                    key={label}
                    className="py-4 flex items-baseline justify-between gap-4"
                    style={{
                      borderBottom: `1px solid ${
                        isDark ? C.brassSoft : C.paperOver
                      }`,
                    }}
                  >
                    <dt
                      className="font-inter text-[10px] uppercase font-semibold"
                      style={{
                        color: C.accentMetal,
                        letterSpacing: "0.28em",
                      }}
                    >
                      {label}
                    </dt>
                    <dd
                      className="font-inter text-[13px] text-right"
                      style={{ color: bodySoft }}
                    >
                      {value}
                    </dd>
                  </div>
                ))}
              </dl>
            </aside>
          )}
        </div>
      </div>
    </section>
  );
}
